import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
    const { user, refresh } = useAuth() || {};
    // Default to light — never use system preference to avoid black screen on mobile
    const [theme, setThemeState] = useState(() => {
        const saved = localStorage.getItem("theme");
        // Only accept explicit "dark" — never inherit from system
        return saved === "dark" ? "dark" : "light";
    });

    useEffect(() => {
        if (user?.theme && user.theme !== theme) {
            setThemeState(user.theme);
            localStorage.setItem("theme", user.theme);
        }
    }, [user?.theme]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const root = document.documentElement;
        if (theme === "dark") {
            root.classList.add("dark");
        } else {
            root.classList.remove("dark");
        }
        root.style.colorScheme = theme;
    }, [theme]);

    const setTheme = async (next) => {
        setThemeState(next);
        localStorage.setItem("theme", next);
        if (user) {
            try { await api.patch("/users/me/preferences", { theme: next }); refresh && refresh(); }
            catch (_) { /* ignore */ }
        }
    };

    const toggle = () => setTheme(theme === "dark" ? "light" : "dark");

    return (
        <ThemeContext.Provider value={{ theme, setTheme, toggle }}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    const ctx = useContext(ThemeContext);
    if (!ctx) return { theme: "light", setTheme: () => {}, toggle: () => {} };
    return ctx;
}